<template>
    <div>
      <AppButton size="small"
                 type="tonal"
                 variant="brand"
                 @click="onOpen"
      >
        {{ $t('project.signing-checklist.pdf-signing.PdfSigningDialogButton.sign') }}
      </AppButton>
      <app-modal v-model="openDialog"
                 size="xl"
                 :ok-text="$t('project.signing-checklist.pdf-signing.PdfSigningDialogButton.load')"
                 :ok-loading="postSignedFilePending"
                 :ok-disabled="!signedDoc || postSignedFilePending"
                 @ok="prepareFileUpload"
                 @cancel="resetAndClose"
      >
        <template #title>{{$t('project.signing-checklist.pdf-signing.PdfSigningDialogButton.sign')}}</template>
        <template #body>
          <p class="text-text-secondary mb-4">{{ fileName }}</p>

          <v-row>
            <v-col>
              <Alert>
                {{ $t('project.signing-checklist.pdf-signing.PdfSigningDialogButton.alertText') }}
              </Alert>
            </v-col>
          </v-row>
          <v-row>
            <v-col >
              <div class="SigningPdfModal-card h-100">
                <v-col class="d-flex align-center">
                  {{ $t('project.signing-checklist.pdf-signing.PdfSigningDialogButton.downloadPagesToSign') }}
                  <AppDownloadFile :link="downloadEnvelopePagesToSign"
                                   class="ml-auto mr-2"
                  >
                    <template #default="{submitForm}">
                      <AppButton color="#333"
                                 class="ml-5"
                                 icon
                                 @click="submitForm">
                        <app-material-icon name="download" color="inherit" />
                      </AppButton>
                    </template>
                  </AppDownloadFile>
                </v-col>
                <v-col class="text-center">
                  <h3>{{ $t('project.signing-checklist.pdf-signing.PdfSigningDialogButton.or') }}</h3>
                </v-col>
                <v-col class="d-flex align-center">
                  {{ $t('project.signing-checklist.pdf-signing.PdfSigningDialogButton.downloadAllPagesToSign') }}
                  <AppDownloadFile :link="downloadAllEnvelopesPagesToSign"
                                   class="ml-auto mr-2"
                  >
                    <template #default="{submitForm}">
                      <AppButton color="#333"
                                 class="ml-5"
                                 icon
                                 @click="submitForm">
                        <app-material-icon name="download" color="inherit" />
                      </AppButton>
                    </template>
                  </AppDownloadFile>
                </v-col>
              </div>
            </v-col>
            <v-col>
              <div class="SigningPdfModal-card h-100">
                <v-col class="text-center">
                  {{$t('project.signing-checklist.pdf-signing.PdfSigningDialogButton.uploadSignedPages')}}
                </v-col>
                <v-col>
                  <AppFileInput
                    v-model="signedDoc"
                    density="compact"
                    prepend-icon="attach_file"
                    clear-icon="close"
                    accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  />
                </v-col>
              </div>
            </v-col>
          </v-row>
          <template v-if="signatureFiles && signatureFiles.signedFiles.length > 0">
            <v-row class="text-primary">
              <v-col>
                {{$t('project.signing-checklist.pdf-signing.PdfSigningDialogButton.signedPages')}}
              </v-col>
            </v-row>
            <v-row class="text-primary">
              <v-col>
                <ul>
                  <li v-for="signedFile in signatureFiles.signedFiles"
                      :key="`signedFile-${signedFile.id}`"
                  >
                    {{ getFileString(signedFile) }}
                    <AppDownloadFile :link="getFileDownloadUrl(signedFile.id)"
                                     class="ml-auto"
                    >
                      <template #default="{submitForm}">
                        <AppButton icon
                                   color="#333"
                                   @click="submitForm"
                        >
                          <app-material-icon name="download" color="inherit" />
                        </AppButton>
                      </template>
                    </AppDownloadFile>
                  </li>
                </ul>
              </v-col>
            </v-row>
          </template>
        </template>
      </app-modal>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import Alert from '@/common/alerts/Alert.vue'
import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppFileInput from '@/common/AppFileInput.vue'
import { ISOToShortDate } from '@/common/utils/dates'
import { toast } from '@/common/utils/toast'
import AppButton from '@/design-system/buttons/AppButton.vue'
import { GET_SIGNATURE_FILES, POST_SIGNED_FILE, RESET_SIGNATURE_FILES } from '@/store/modules/signing-checklist/action_types'

export default {
  name: 'PdfSigningDialogButton',
  components: { AppFileInput, Alert, AppButton, AppDownloadFile },
  props: {
    step: {
      type: Object,
      required: true,
    },
    signerId: {
      type: Number,
      required: true,
    },
  },
  data () {
    return {
      openDialog: false,
      signedDoc: null,
    }
  },
  computed: {
    ...mapState('signingChecklist', ['signatureFiles', 'postSignedFilePending']),
    fileName () {
      if (this.step) {
        return this.step.hasOwnProperty('envelope')
          ? this.step.envelope.file.basename
          : this.step.name || null
      }
      return null
    },
    downloadEnvelopePagesToSign () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.$route.params.mnemo}/downloads/envelopePdfFileToSign/${this.step.envelope.id}`
    },
    downloadAllEnvelopesPagesToSign () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.$route.params.mnemo}/downloads/envelopePdfFileToSign`
    },
  },
  async mounted () {
    this.GET_SIGNATURE_FILES({
      mnemo: this.$route.params.mnemo,
      envelopeId: this.step.envelope.id,
      signerId: this.signerId,
    })
  },
  methods: {
    ...mapActions('signingChecklist', [GET_SIGNATURE_FILES, POST_SIGNED_FILE, RESET_SIGNATURE_FILES]),
    onOpen () {
      this.openDialog = true
      this.GET_SIGNATURE_FILES({
        mnemo: this.$route.params.mnemo,
        envelopeId: this.step.envelope.id,
        signerId: this.signerId,
      })
    },
    getFileDownloadUrl (fileId) {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.$route.params.mnemo}/downloads/envelopePdfSignedFile/${this.step.envelope.id}/${fileId}`
    },
    getFileString (file) {
      let str = ''
      if (file.fileName) {
        str += file.fileName
      }
      if (file.signerName) {
        str += `, ${this.$t('project.signing-checklist.pdf-signing.PdfSigningDialogButton.signerName')} ${file.signerName}`
      }
      if (file.date) {
        str += `, ${this.$t('project.signing-checklist.pdf-signing.PdfSigningDialogButton.signDate')} ${ISOToShortDate(file.date)}`
      }
      return str
    },
    async prepareFileUpload () {
      try {
        const formData = new FormData()
        formData.append('signed_pages', this.signedDoc, this.signedDoc.name)
        formData.append('signer_id', this.signerId)
        await this.POST_SIGNED_FILE({
          mnemo: this.$route.params.mnemo,
          envelopeId: this.step.envelope.id,
          formData,
        })
        toast.success(this.$t('project.signing-checklist.pdf-signing.PdfSigningDialogButton.postSignedFileSuccess'))
        this.resetAndClose()
        this.GET_SIGNATURE_FILES({
          mnemo: this.$route.params.mnemo,
          envelopeId: this.step.envelope.id,
          signerId: this.signerId,
        })
      } catch (e) {
        toast.error(this.$t('project.signing-checklist.pdf-signing.PdfSigningDialogButton.postSignedFileError'))
      }
    },
    resetAndClose () {
      this.openDialog = false
      this.signedDoc = null
      this.RESET_SIGNATURE_FILES()
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.SigningPdfModal-card {
  @include border-dashed(rgb(var(--v-theme-grey-500)));
  @include border-radius('border-radius-3');
}
</style>
